import type { Provider } from '../../types/provider';
import { ProviderRow } from './ProviderRow';
import { EmptyState } from '../shared/EmptyState';
import { LoadingState } from '../shared/LoadingState';
import { PROVIDER_NAME_COL_MIN_CLASS } from './provider-name-col';

interface ProvidersTableViewProps {
  readonly providers: ReadonlyArray<Provider>;
  readonly total: number;
  readonly isLoadingMore: boolean;
  readonly hasMore: boolean;
  /**
   * Optional per-row click handler. When passed, each row becomes
   * keyboard-activatable and fires this callback with the provider
   * -- the page-level RowPeekDrawer wiring uses this to open the
   * peek panel.
   */
  readonly onRowSelect?: (provider: Provider) => void;
  /**
   * Ref for the bottom sentinel element observed by an
   * IntersectionObserver in the parent. Rendered at the very
   * bottom of the table so the observer fires shortly before the
   * user reaches the end of the list.
   */
  readonly sentinelRef: React.Ref<HTMLDivElement>;
  /** Optional handler for the empty-state action ("Clear filters"). */
  readonly onClearFilters?: () => void;
  /**
   * IMP-005 -- active occupational group filter. Passed through to
   * each ProviderRow so the "Programs" cell drills down to only
   * that group's programs when filtered. Null/undefined preserves
   * the existing behaviour.
   */
  readonly activeOgName?: string | null;
}

/**
 * Columns reference — kept in one place so a future column add or
 * relabel touches a single array. Aligned with the column-cell
 * rendering in `ProviderRow`. NO sortable headers — backend pre-sorts
 * by `totalAwards` desc and the UX is filter-only.
 */
type ColumnSpec = {
  label: string;
  align?: 'right' | 'center' | 'left';
  widthClass?: string;
  /** Applied to <th> alongside structural classes — e.g. sticky-left. */
  extraClass?: string;
  /** Optional one-line caption rendered under the label (smaller +
   *  muted). Used for affordance hints like "click to see detailed
   *  view" on the Programs column. */
  hint?: string;
};

// 2026-06-05 (Maria) relabels: the prior "Occ groups" + "Springboard
// share" abbreviations under-communicated what the columns measured.
// Both labels now spell out the springboard framing explicitly; widths
// bumped so the longer copy can wrap to ~2 lines under the `h-9` row
// without overflowing the cell.
//
// Per-column gates (also 2026-06-05, Maria) -- two complementary
// columns are conditional on the OG filter:
//   - `springboardOccupationalGroupsOnly: true`
//     Visible only when NO specific OG is selected ("All occupational
//     groups"). The count makes sense as a breadth signal across the
//     full set; once a single OG is in scope the count is always 1
//     and adds nothing.
//   - `springboardShareOnly: true`
//     Visible only when a specific OG IS selected. The cross-OG share
//     percentage is hard to interpret without a single anchor group.
//
// In practice the gates are inverses, so the table swaps one column
// for the other as the user toggles the OG filter.
type ColumnSpecWithGate = ColumnSpec & {
  readonly springboardOccupationalGroupsOnly?: boolean;
  readonly springboardShareOnly?: boolean;
};

// 2026-07-02 (V2 feedback): column headers renamed per Georgetown's
// "recurring column name copy" spec.
//   Metro                         -> Metro area
//   Total awards                  -> Annual credentials awarded
//   Springboard occupational groups (already correct)
//   Programs                      -> Springboard occupation-aligned programs
//
// CR-07 (2026-07-08, Maria 6a / client G2): every column is left-
// aligned, header and content alike -- the numeric columns were
// previously right-aligned while their headers stayed left-aligned,
// the exact mismatch the client flagged. `align: 'left'` is explicit
// below (rather than omitted) so the column set stays self-documenting.
const COLUMNS: ReadonlyArray<ColumnSpecWithGate> = [
  { label: 'Provider', align: 'left', widthClass: PROVIDER_NAME_COL_MIN_CLASS, extraClass: 'sticky left-0 z-[2] bg-white' },
  { label: 'Metro area', align: 'left', widthClass: 'w-[200px]' },
  { label: 'Annual credentials awarded', align: 'left', widthClass: 'w-[160px]' },
  {
    label: 'Springboard occupational groups',
    align: 'left',
    widthClass: 'w-[170px]',
    springboardOccupationalGroupsOnly: true,
  },
  {
    label: 'Springboard occupation-aligned programs',
    align: 'left',
    widthClass: 'w-[200px]',
    hint: 'Click a row to see detailed view',
  },
  {
    label: "Provider's production share of credentials aligned with springboard occupations",
    align: 'left',
    widthClass: 'w-[220px]',
    springboardShareOnly: true,
  },
];

function alignClass(align: ColumnSpec['align']) {
  if (align === 'right') return 'text-right';
  if (align === 'center') return 'text-center';
  return 'text-left';
}

/**
 * Renders the providers table (header + body) plus the sentinel +
 * "loading more" / "end of list" affordances at the bottom.
 *
 * Empty + loading states are surfaced here so the parent page is just
 * data-fetching glue — keeps the render branches close to the column
 * definitions they apply to.
 */
export function ProvidersTableView({
  providers,
  total,
  isLoadingMore,
  hasMore,
  sentinelRef,
  onClearFilters,
  onRowSelect,
  activeOgName,
}: ProvidersTableViewProps) {
  if (providers.length === 0) {
    return (
      <EmptyState
        title="No providers match"
        description="Try clearing the search or filter selections."
        action={
          onClearFilters && (
            <button
              type="button"
              onClick={onClearFilters}
              className="text-xs font-semibold text-blue-link hover:underline"
            >
              Clear filters
            </button>
          )
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between text-[0.8rem] font-medium text-text-3">
        <span>
          Showing {providers.length.toLocaleString()} of {total.toLocaleString()}{' '}
          {total === 1 ? 'provider' : 'providers'}
        </span>
      </div>

      {/* 2026-07-03 (V2 feedback follow-up): reviewers asked for the
          main providers table to be "static" so the page's own
          scrollbar doesn't keep growing as more rows load. Cap the
          rows viewport at 60vh and make it scroll internally — the
          header row stays sticky at the top of the viewport so
          column labels don't disappear as the user scrolls. The
          sentinel that drives infinite-loading is unchanged; the
          IntersectionObserver just observes inside a smaller
          scrollable container now. */}
      <div className="overflow-hidden rounded-lg border border-border bg-white">
        <div className="max-h-[60vh] overflow-x-auto overflow-y-auto">
          <table className="providers-table w-full" aria-label="Education providers">
            {/* Sticky header inside the scrollable container so column
                labels stay pinned at the top as the user scrolls the
                rows. sticky top-0 within a `max-h-[60vh]
                overflow-y-auto` parent gives the correct "freeze the
                header, scroll the body" behavior without needing a
                separate <div>-based header row. */}
            <thead className="sticky top-0 z-[2]">
              <tr>
                {COLUMNS.filter((col) => {
                  // Hide the share column when no OG filter is active.
                  if (col.springboardShareOnly && !activeOgName) return false;
                  // Hide the OG-count column when an OG filter IS active.
                  if (col.springboardOccupationalGroupsOnly && activeOgName) return false;
                  return true;
                }).map((col) => {
                  // CR-01 (2026-07-07): the "Annual credentials
                  // awarded" cell now shows the REAL per-group figure
                  // (`provider.awardsByOccGroup[activeOgName]`) when an
                  // OG filter is active -- see ProviderRow. Replace the
                  // old "per-group figure pending" caveat with a
                  // concise, honest hint that the number is scoped to
                  // the selected group.
                  const hint =
                    col.label === 'Annual credentials awarded' && activeOgName
                      ? 'Within selected group'
                      : col.hint;
                  return (
                  <th
                    key={col.label}
                    scope="col"
                    className={`select-none border-b-2 border-navy bg-white px-3 py-2 align-middle text-[13px] font-semibold text-text-3 ${alignClass(col.align)} ${col.widthClass ?? ''} ${col.extraClass ?? ''}`}
                  >
                    {col.label}
                    {hint && (
                      <span className="mt-0.5 block text-[10px] font-normal italic leading-[1.3] text-text-4">
                        {hint}
                      </span>
                    )}
                  </th>
                  );
                })}
              </tr>
            </thead>
            <tbody aria-label="Provider rows">
              {providers.map((p) => (
                <ProviderRow
                  key={p.unitid}
                  provider={p}
                  onSelect={onRowSelect}
                  activeOgName={activeOgName}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Sentinel + tail status. The sentinel always renders so the
          parent's IntersectionObserver has a stable observation
          target; the surrounding label switches between
          "loading more", "end of list", or empty.
          SUGG #6 — wrap status text in aria-live="polite" so screen
          readers are announced once pagination ends (without
          interrupting whatever the user is currently reading). */}
      <div
        className="flex flex-col items-center gap-1 py-2 text-xs text-text-4"
        aria-live="polite"
        aria-atomic="true"
      >
        <div ref={sentinelRef} aria-hidden="true" />
        {isLoadingMore && <LoadingState label="Loading more providers…" />}
        {!hasMore && !isLoadingMore && providers.length > 0 && (
          <span>End of list — {total.toLocaleString()} providers total.</span>
        )}
      </div>
    </div>
  );
}
